import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, FormControl, Grid, IconButton, InputAdornment, InputLabel,
    Paper, Select, Snackbar, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { AddCircle as IconAddCircle, Delete as IconDelete, Edit as IconEdit, Search as IconSearch } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { useSelector } from "react-redux";
import * as filialService from "../../services/filialService";

import atencionService from "../../services/atencionService";
import diagnosticoService from "../../services/diagnosticoService";
import comprobanteService from "../../services/comprobanteService";
import MantenimientoForm from "../../components/common/MantenimientoForm";
import DialogYesNo from "../../components/DialogYesNo";
import DialogoBusqueda from "../../components/common/DialogoBusqueda";
import PersonSearch from "../../components/PersonSearch";
import ComprobanteDialogo from "../../components/ComprobanteDialogo";
import ComprobanteMantenimientoDialogo from "../../components/ComprobanteMantenimientoDialogo";
import PersonRegister from "../../components/PersonRegister";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Mantenimiento de una atencion en una sola pantalla: los datos de la atencion
// y, debajo, el detalle con los servicios prestados.
//
// La atencion no tiene tipo de servicio propio: lo que se presto se ve en el
// detalle. Cada fila del detalle es un servicio de la tabla servicio y se
// completa en la misma fila, con el comprobante con el que se facturo.
//
// Los servicios especiales (Terapia y Diagnostico) no se escriben aca: se crean
// en su propio mantenimiento, que ya deja la fila del detalle referenciada. Por
// eso el detalle ofrece "Nueva Terapia" y "Nuevo Diagnostico" en vez de un
// boton Nuevo.

const ENTIDAD_NUEVA = {
    id: 0,
    idPersona: 0,
    idFilial: 0,
    persona: "",
    fecha: "",
    observaciones: "",
    idEstado: 2,
    estado: "",
    cantidadDetalles: 0,
};

const Edit = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { isReadOnly } = useMantenimiento("atencion");
    const idFilialUsuario = useSelector((state) => state.seguridad?.idFilial) || 0;
    const [listFiliales, setListFiliales] = useState([]);

    const nuevo = params.id == 0;

    const [entity, setEntity] = useState(ENTIDAD_NUEVA);
    const [listServicios, setListServicios] = useState([]);

    const [guardando, setGuardando] = useState(false);

    // Detalle de la atencion.
    const [detalles, setDetalles] = useState([]);
    const [detallesOriginales, setDetallesOriginales] = useState([]);
    const [cargandoDetalles, setCargandoDetalles] = useState(false);

    // Id temporal (negativo) para las filas nuevas que aun no se guardan.
    const tempIdRef = React.useRef(1);

    // Lupa y registro/edicion de la persona.
    const [buscandoPersona, setBuscandoPersona] = useState(false);
    const [openPersonRegister, setOpenPersonRegister] = useState(false);
    const [personas, setPersonas] = useState([]);
    const [filtroPersonas, setFiltroPersonas] = useState({ numeroDocumento: "", nombres: "" });

    // Comprobante de una fila del detalle: se registra con el boton de agregar o
    // se busca con la lupa, y con el ojo se consulta.
    const [buscandoComprobante, setBuscandoComprobante] = useState(false);
    const [detalleComprobante, setDetalleComprobante] = useState(null);
    const [items, setItems] = useState([]);
    const [filtrosComprobante, setFiltrosComprobante] = useState({ serie: "", concepto: "", idEstadoPago: 0 });
    const [viendoComprobante, setViendoComprobante] = useState(false);
    const [detalleViendo, setDetalleViendo] = useState(null);
    const [registrandoComprobante, setRegistrandoComprobante] = useState(false);
    const [detalleRegistrando, setDetalleRegistrando] = useState(null);

    const [openDialogEliminar, setOpenDialogEliminar] = useState(false);
    const [detalleSeleccionado, setDetalleSeleccionado] = useState(null);

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const avisar = (severity, message) => {
        setPropsMessage({ severity, message });
        setOpenMessage(true);
    }

    const idAtencion = parseInt(params.id || "0");

    const fetchEntity = useCallback(async () => {
        if (idAtencion <= 0) {
            setEntity({ ...ENTIDAD_NUEVA, idFilial: idFilialUsuario > 0 ? idFilialUsuario : 1, fecha: moment().format('yyyy-MM-DD') });
            return;
        }

        const lista = await atencionService.getsAtencionView()
            .catch((err) => { console.log("Err: ", err); return []; });

        const encontrada = (lista || []).find((x) => x.id == idAtencion);

        if (!encontrada) {
            avisar("error", "No se encontro la atencion.");
            return;
        }

        setEntity({ ...encontrada, idFilial: encontrada.idFilial || (idFilialUsuario > 0 ? idFilialUsuario : 1), fecha: moment(encontrada.fecha).format('yyyy-MM-DD') });
    }, [idAtencion])

    const fetchListServicios = useCallback(async () => {
        const data = await diagnosticoService.getsServicios().catch(() => []);
        setListServicios(data || []);
    }, [])

    const fetchDetalles = useCallback(async () => {
        if (idAtencion <= 0) {
            setDetalles([]);
            return;
        }

        setCargandoDetalles(true);

        const lista = await atencionService.getsAtencionDetallePorAtencion(idAtencion).catch(() => []);

        setDetalles(lista || []);
        setDetallesOriginales(lista || []);
        setCargandoDetalles(false);
    }, [idAtencion])

    useEffect(() => {
        fetchEntity();
        fetchListServicios();
        filialService.getFilialesList().then((data) => setListFiliales(filialService.soloFilialDelUsuario(data, idFilialUsuario))).catch(() => {});
    }, [idFilialUsuario])

    useEffect(() => {
        fetchDetalles();
    }, [idAtencion])

    // Un servicio es especial si su tipo lo marca como especial (Terapia y
    // Diagnostico). En esos se crea el detalle en su propio mantenimiento; en los
    // no especiales se gestiona aca, dentro de la atencion.
    const esEspecial = (idTipoServicio) => {
        const s = listServicios.find((x) => x.id == idTipoServicio);
        return s && s.especial === true;
    };

    // Alta de una fila del detalle. Se elige el servicio y se completa el
    // comprobante en la misma fila, asi que aqui solo se agrega la fila vacia.
    const agregarDetalle = () => {
        const noEspeciales = listServicios.filter((s) => s.especial !== true);

        if (noEspeciales.length === 0) {
            avisar("warning", "No hay servicios para agregar al detalle.");
            return;
        }

        setDetalles((prev) => [...prev, {
            idAtencionDetalle: -(tempIdRef.current++),
            idAtencion,
            idTipoServicio: noEspeciales[0].id,
            tipoServicio: noEspeciales[0].descripcion,
            idReferencia: null,
            idComprobante: null,
            comprobante: "",
            fechaRegistro: new Date().toISOString(),
        }]);
    }

    const cambiarServicio = (detalle, idTipoServicio) => {
        const servicio = listServicios.find((x) => x.id == idTipoServicio);

        cambiarDetalle(detalle, {
            idTipoServicio,
            tipoServicio: servicio ? servicio.descripcion : "",
            // El comprobante pertenece a un servicio concreto: al cambiar de
            // servicio se suelta la referencia para no dejar el comprobante de otro.
            idComprobante: null,
            comprobante: "",
        });
    }

    const cambiarDetalle = (detalle, cambios) => {
        setDetalles((prev) => prev.map((d) =>
            d.idAtencionDetalle === detalle.idAtencionDetalle ? { ...d, ...cambios } : d
        ));
    }

    // Alta de los servicios especiales: cada uno en su propio mantenimiento, que
    // ya deja la fila del detalle referenciada. Primero se guarda la atencion
    // para usar el id que queda registrado.
    const nuevaTerapia = async () => {
        const id = await guardarAtencion();
        if (!id) return;
        navigate("/therapy/edit/0?idAtencion=" + id);
    };

    const nuevoDiagnostico = async () => {
        const id = await guardarAtencion();
        if (!id) return;
        navigate("/diagnostico/edit/" + id + "/0");
    };

    // Alta del comprobante de una fila del detalle: se abre el mantenimiento en
    // un dialogo y, al guardar, la fila queda con el comprobante.
    const registrarComprobante = (detalle) => {
        setDetalleRegistrando(detalle);
        setRegistrandoComprobante(true);
    }

    const comprobanteRegistrado = (idComprobante) => {
        if (detalleRegistrando) {
            cambiarDetalle(detalleRegistrando, {
                idComprobante,
                comprobante: "Comprobante " + idComprobante,
            });
        }

        setRegistrandoComprobante(false);
        setDetalleRegistrando(null);
    }

    const buscarItems = async () => {
        const lista = await comprobanteService.getsComprobanteDetalleBusqueda({
            serie: filtrosComprobante.serie,
            numero: "",
            concepto: filtrosComprobante.concepto,
            idEstadoPago: filtrosComprobante.idEstadoPago,
            idEstado: 0,
        }).catch((err) => { console.log("Err: ", err); return []; });

        setItems(lista || []);
    }

    const elegirComprobante = (item) => {
        if (!detalleComprobante) return;

        cambiarDetalle(detalleComprobante, {
            idComprobante: item.idComprobante,
            comprobante: item.serieNumero + " - Item " + String(item.numero).padStart(3, "0"),
        });

        setBuscandoComprobante(false);
        setDetalleComprobante(null);
    }

    const buscarPersonas = async () => {
        const datos = await import("../../services/personaService");

        const lista = await datos
            .getPersonaResumenBasicoViewByNumeroDocumentoYNombres(filtroPersonas.numeroDocumento, filtroPersonas.nombres)
            .catch((err) => { console.log("Err: ", err); return []; });

        setPersonas(lista || []);
    }

    const elegirPersona = (persona) => {
        // La vista de personas trae dos identificadores: Id (interno de la vista) e
        // IdPersona, que es el que espera atencion.IdPersona.
        const id = persona.idPersona ?? persona.id;
        setEntity((prev) => ({ ...prev, idPersona: id, persona: persona.nombres }));
        setBuscandoPersona(false);
    }

    const onPersonSaved = (id, nombres) => {
        setEntity((prev) => ({ ...prev, idPersona: id, persona: nombres }));
        setOpenPersonRegister(false);
    }

    const eliminar = () => {
        if (!detalleSeleccionado) return;

        // Quitar el detalle: se quita de la lista en pantalla; el cambio se
        // confirma con el boton Guardar, que es el que persiste en el backend.
        setDetalles((prev) => prev.filter((d) => d.idAtencionDetalle !== detalleSeleccionado.idAtencionDetalle));
        setDetalleSeleccionado(null);
        setOpenDialogEliminar(false);
    }

    // Aplica al backend los cambios del detalle, solo de servicios NO especiales
    // (que se gestionan en esta pantalla). Los especiales se crean y anulan en su
    // propio mantenimiento.
    const guardarDetalles = async (idAtencionGuardada) => {
        // Quitar: las originales que ya no estan.
        for (const original of detallesOriginales) {
            if (esEspecial(original.idTipoServicio)) continue;

            const sigue = detalles.some((d) => d.idAtencionDetalle === original.idAtencionDetalle);
            if (!sigue) {
                await atencionService.deleteAtencionDetalle(original.idAtencionDetalle)
                    .catch((err) => { console.log("Err: ", err); return false; });
            }
        }

        // Agregar los nuevos y actualizar los editados.
        for (const det of detalles) {
            if (esEspecial(det.idTipoServicio)) continue;

            const original = det.idAtencionDetalle > 0
                ? detallesOriginales.find((o) => o.idAtencionDetalle === det.idAtencionDetalle)
                : null;

            const cambio = !!original
                && (original.idTipoServicio !== det.idTipoServicio || (original.idComprobante || null) !== (det.idComprobante || null));

            if (!original || cambio) {
                await atencionService.addAtencionDetalle({
                    idAtencionDetalle: det.idAtencionDetalle > 0 ? det.idAtencionDetalle : 0,
                    idAtencion: idAtencionGuardada,
                    idTipoServicio: det.idTipoServicio,
                    idReferencia: det.idReferencia || null,
                    idComprobante: det.idComprobante || null,
                }).catch((err) => { console.log("Err: ", err); return 0; });
            }
        }
    }

    // Guarda la cabecera y el detalle. Devuelve el id o 0 si una validacion
    // o el guardado no dejan continuar.
    const guardarAtencion = async () => {
        if ((entity.idPersona || 0) <= 0) {
            avisar("error", "Indique la persona de la atencion.");
            return 0;
        }

        setGuardando(true);

        const filialFinal = entity.idFilial || (idFilialUsuario > 0 ? idFilialUsuario : 1);
        const id = await atencionService.addUpdateAtencion({ ...entity, idFilial: filialFinal })
            .catch((err) => { console.log("Err: ", err); return 0; });

        if (!id) {
            setGuardando(false);
            avisar("error", "No se pudo guardar la atencion.");
            return 0;
        }

        // El detalle se guarda junto a la cabecera, y solo en los servicios
        // no especiales que se gestionan aqui.
        await guardarDetalles(id);
        setGuardando(false);
        return id;
    }

    const postEntity = async () => {
        const id = await guardarAtencion();
        if (!id) return;

        avisar("success", "Se guardaron los datos de la atencion con exito.");

        if (nuevo) {
            navigate("/atencion/edit/" + id, { replace: true });
        }
        else {
            fetchDetalles();
        }
    }

    const columnasItems = [
        { field: 'serieNumero', headerName: 'Serie-Numero', width: 130, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'fecha', headerName: 'Fecha', width: 110, type: 'date', sortable: false, headerAlign: 'center', align: 'center'
            , valueFormatter: (value) => moment(value).format('DD/MM/YYYY') },
        { field: 'cantidad', headerName: 'Cantidad', width: 90, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'conceptoCobro', headerName: 'Concepto de Cobro', width: 260, sortable: false, headerAlign: 'left' },
        { field: 'subTotal', headerName: 'Sub Total', width: 100, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'estadoPago', headerName: 'Estado Pago', width: 120, sortable: false, headerAlign: 'center', align: 'center' },
    ];

    return (
        <div>
            <MantenimientoForm
                title={(nuevo ? "Nueva" : "Editar") + " Atencion"}
                backTo={"/atencion"}
                onSave={postEntity}
                disabled={isReadOnly}
            >
                <Grid container spacing={1} sx={{
                    "& .MuiGrid-root>.MuiGrid-item": {
                        paddingTop: "8px"
                    },
                }}>
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <Box sx={{ fontWeight: 'bold' }} padding={0}>
                                    <Typography variant="h7">Datos de la Atencion</Typography>
                                </Box>
                                <Grid container spacing={1} alignItems={"center"}>
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            required
                                            name="persona"
                                            label="Persona"
                                            fullWidth
                                            value={entity.persona}
                                            size="small"
                                            disabled={isReadOnly || guardando}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title={entity.idPersona > 0 ? "Editar Persona" : "Registrar Persona"} arrow>
                                                            <span>
                                                                <IconButton
                                                                    aria-label="registrar o editar persona"
                                                                    size="small"
                                                                    edge="end"
                                                                    disabled={isReadOnly || guardando || (entity.idPersona <= 0 && !nuevo)}
                                                                    onClick={() => setOpenPersonRegister(true)}
                                                                >
                                                                    {entity.idPersona > 0 ? <IconEdit fontSize="small" /> : <IconAddCircle fontSize="small" />}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title={"Buscar persona"} arrow>
                                                            <span>
                                                                <IconButton
                                                                    aria-label={"buscar persona"}
                                                                    size="small"
                                                                    edge="end"
                                                                    disabled={isReadOnly || guardando || !nuevo}
                                                                    onClick={() => setBuscandoPersona(true)}
                                                                >
                                                                    <IconSearch fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl required fullWidth size="small">
                                            <InputLabel htmlFor="input-atencion-filial">Filial</InputLabel>
                                            <Select
                                                native
                                                label="Filial"
                                                value={entity.idFilial || (idFilialUsuario > 0 ? idFilialUsuario : 1)}
                                                onChange={(e) => setEntity({ ...entity, idFilial: parseInt(e.target.value, 10) || 1 })}
                                                inputProps={{ name: 'idFilial', id: 'input-atencion-filial' }}
                                                disabled={isReadOnly || guardando || idFilialUsuario > 0}
                                                size="small"
                                            >
                                                {idFilialUsuario <= 0 && <option key={0} value={0}>{"(Seleccione)"}</option>}
                                                {listFiliales.map((row) => (
                                                    <option key={row.id} value={row.id}>{row.descripcion || row.nombre}</option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <TextField
                                            required
                                            name="fecha"
                                            label="Fecha"
                                            type="date"
                                            fullWidth
                                            value={entity.fecha}
                                            onChange={(event) => setEntity({ ...entity, fecha: event.target.value })}
                                            disabled={isReadOnly || guardando}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <TextField
                                            name="observaciones"
                                            label="Observaciones"
                                            fullWidth
                                            multiline
                                            value={entity.observaciones || ""}
                                            onChange={(event) => setEntity({ ...entity, observaciones: event.target.value })}
                                            disabled={isReadOnly || guardando}
                                            size="small"
                                            InputLabelProps={{ shrink: true, sx: { overflow: "visible", lineHeight: "1.6" } }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Detalle de la atencion: una fila por servicio, con el
                        comprobante con el que se facturo. */}
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <Grid container alignItems={"center"}>
                                    <Grid item>
                                        <Box sx={{ fontWeight: 'bold' }} padding={0}>
                                            <Typography variant="h7">Detalle de la Atencion</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {/* Agregar una fila vacia al detalle: el
                                                servicio y el comprobante se completan
                                                en la misma fila. */}
                                            <Tooltip title={"Agregar servicio al detalle"} arrow>
                                                <span>
                                                    <IconButton aria-label={"agregar servicio"} size="small"
                                                        disabled={isReadOnly || guardando}
                                                        onClick={agregarDetalle}>
                                                        <IconAddCircle fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {/* Alta de Terapia y de Diagnostico. Al
                                                usarlas se guarda primero la atencion,
                                                con sus validaciones, y se abre el
                                                mantenimiento con el id guardado. */}
                                            <Button variant={"outlined"} size={"small"} startIcon={<IconAddCircle />}
                                                disabled={isReadOnly || guardando}
                                                onClick={nuevaTerapia}>
                                                {"NUEVA TERAPIA"}
                                            </Button>
                                            <Button variant={"outlined"} size={"small"} startIcon={<IconAddCircle />}
                                                disabled={isReadOnly || guardando}
                                                onClick={nuevoDiagnostico}>
                                                {"NUEVO DIAGNOSTICO"}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <TableContainer component={Paper} variant="outlined" sx={{ marginTop: "12px" }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: "#1976d2" }}>
                                                <TableCell sx={{ color: "white", width: "50px" }} align="center">N°</TableCell>
                                                <TableCell sx={{ color: "white" }}>Tipo de Servicio</TableCell>
                                                <TableCell sx={{ color: "white", width: "80px" }} align="center">Id</TableCell>
                                                <TableCell sx={{ color: "white" }}>Comprobante</TableCell>
                                                <TableCell sx={{ color: "white", width: "140px" }}>Fecha Registro</TableCell>
                                                <TableCell sx={{ color: "white", width: "120px" }} align="center">Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {detalles.map((row, index) => {
                                                // Los servicios especiales solo se
                                                // consultan: se editan en su propio
                                                // mantenimiento.
                                                const editable = !esEspecial(row.idTipoServicio);

                                                return (
                                                    <TableRow key={row.idAtencionDetalle}>
                                                        <TableCell align="center">{index + 1}</TableCell>
                                                        <TableCell>
                                                            {editable ? (
                                                                <FormControl fullWidth>
                                                                    <Select
                                                                        native
                                                                        label="Tipo de Servicio"
                                                                        value={row.idTipoServicio}
                                                                        onChange={(event) => cambiarServicio(row, parseInt(event.target.value))}
                                                                        inputProps={{ 'aria-label': 'tipo de servicio del detalle' }}
                                                                        size="small"
                                                                        disabled={isReadOnly || guardando}
                                                                    >
                                                                        <option key={0} value={0}>{"(Seleccione)"}</option>
                                                                        {listServicios.filter((s) => s.especial !== true).map((s) => (
                                                                            <option key={s.id} value={s.id}>{s.descripcion}</option>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            ) : (
                                                                row.tipoServicio
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {row.idReferencia != null ? row.idReferencia : ""}
                                                        </TableCell>
                                                        <TableCell>
                                                            {editable ? (
                                                                <Box display="flex" alignItems="center" gap={0}>
                                                                    <TextField
                                                                        name={"comprobante" + row.idAtencionDetalle}
                                                                        label="Comprobante"
                                                                        value={row.comprobante || ""}
                                                                        fullWidth
                                                                        size="small"
                                                                        disabled
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                    {/* Registrar o buscar el
                                                                        comprobante con el que se
                                                                        factura el servicio. */}
                                                                    <Tooltip title={(row.idComprobante ? "Editar" : "Registrar") + " comprobante"} arrow>
                                                                        <span>
                                                                            <IconButton aria-label={"registrar comprobante"} size="small"
                                                                                disabled={isReadOnly || guardando}
                                                                                onClick={() => registrarComprobante(row)}>
                                                                                {row.idComprobante ? <IconEdit /> : <IconAddCircle />}
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                    <Tooltip title={"Buscar comprobante"}>
                                                                        <span>
                                                                            <IconButton aria-label={"buscar comprobante"} size="small"
                                                                                disabled={isReadOnly || guardando}
                                                                                onClick={() => { setDetalleComprobante(row); setBuscandoComprobante(true); }}>
                                                                                <IconSearch />
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                </Box>
                                                            ) : (
                                                                // En los servicios especiales el comprobante se ve, pero
                                                                // los controles no aparecen: se registra en el
                                                                // mantenimiento del propio servicio.
                                                                (row.comprobante || "")
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.fechaRegistro ? moment(row.fechaRegistro).format('DD/MM/YYYY HH:mm') : ""}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {row.idComprobante != null && (
                                                                <Tooltip title={"Ver comprobante"}>
                                                                    <span>
                                                                        <IconButton aria-label={"ver comprobante"} size="small"
                                                                            onClick={() => { setDetalleViendo(row); setViendoComprobante(true); }}>
                                                                            <IconSearch fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                            {editable && (
                                                                <Tooltip title={"Quitar servicio"}>
                                                                    <span>
                                                                        <IconButton aria-label={"quitar servicio"} size="small"
                                                                            disabled={isReadOnly}
                                                                            onClick={() => { setDetalleSeleccionado(row); setOpenDialogEliminar(true); }}>
                                                                            <IconDelete fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            {detalles.length === 0 && !cargandoDetalles && (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center">
                                                        {"No hay servicios registrados"}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Typography variant="body2" color="textSecondary" sx={{ marginTop: "8px" }}>
                                    {"Los servicios de Terapia y Diagnostico se registran con Nueva Terapia y Nuevo Diagnostico, y quedan referenciados en el detalle."}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </MantenimientoForm>

            {/* Lupa de la persona de la atencion (ventana estandar). */}
            <PersonSearch
                open={buscandoPersona}
                openClose={() => setBuscandoPersona(false)}
                setSelection={elegirPersona}
            />

            {/* Lupa del comprobante: se elige el item con el que se factura. */}
            <DialogoBusqueda
                open={buscandoComprobante}
                onClose={() => { setBuscandoComprobante(false); setDetalleComprobante(null); }}
                titulo="Buscar Detalle del Comprobante"
                columns={columnasItems}
                rows={items}
                rowKey={(row) => row.idComprobante + "-" + row.numero}
                onAceptar={elegirComprobante}
                filtros={
                    <Grid container spacing={1} alignItems={"center"}>
                        <Grid item xs={4} sm={2}>
                            <TextField name="serie" label="Serie-Numero" value={filtrosComprobante.serie} fullWidth
                                onChange={(event) => setFiltrosComprobante({ ...filtrosComprobante, serie: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscarItems(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={8} sm={4}>
                            <TextField name="concepto" label="Concepto de Cobro" value={filtrosComprobante.concepto} fullWidth
                                onChange={(event) => setFiltrosComprobante({ ...filtrosComprobante, concepto: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscarItems(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <FormControl fullWidth>
                                <InputLabel htmlFor="input-estadopagodetalle">Estado Pago</InputLabel>
                                <Select
                                    native
                                    label="Estado Pago"
                                    value={filtrosComprobante.idEstadoPago}
                                    onChange={(event) => setFiltrosComprobante({ ...filtrosComprobante, idEstadoPago: parseInt(event.target.value) })}
                                    inputProps={{ name: 'idEstadoPago', id: 'input-estadopagodetalle' }}
                                    size="small"
                                >
                                    <option key={0} value={0}>{"(Todos)"}</option>
                                    <option key={144} value={144}>{"Pendiente"}</option>
                                    <option key={145} value={145}>{"Cancelado"}</option>
                                    <option key={227} value={227}>{"Pago Consolidado"}</option>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                            <Button variant={"outlined"} size={"medium"} fullWidth onClick={() => { buscarItems(); }}>
                                <IconSearch />
                            </Button>
                        </Grid>
                    </Grid>
                }
            />

            {/* Consulta del comprobante con el que se facturo el servicio. */}
            <ComprobanteDialogo
                open={viendoComprobante}
                idComprobante={detalleViendo?.idComprobante || 0}
                onClose={() => setViendoComprobante(false)}
            />

            {/* Alta del comprobante de una fila del detalle. */}
            <ComprobanteMantenimientoDialogo
                open={registrandoComprobante}
                onClose={() => { setRegistrandoComprobante(false); setDetalleRegistrando(null); }}
                onSaved={comprobanteRegistrado}
            />

            {/* Registro y edicion de la persona agrupado al control */}
            <PersonRegister
                id={entity.idPersona || 0}
                sliceKey="atencionPersonRegister"
                idTipoPersona={0}
                tipoPersona=""
                open={openPersonRegister}
                openClose={() => setOpenPersonRegister(false)}
                setNewPersonSaved={onPersonSaved}
            />

            <DialogYesNo
                open={openDialogEliminar}
                titulo={"Quitar Servicio"}
                texto={"Se quitara el servicio del detalle de la atencion. Desea continuar?"}
                setYes={eliminar}
                setNo={() => { setDetalleSeleccionado(null); }}
                openClose={() => {
                    setOpenDialogEliminar(!openDialogEliminar);

                    if (openDialogEliminar) setDetalleSeleccionado(null);
                }}
            />

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(!openMessage)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(!openMessage)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default Edit;